
import {Component, Emit, Prop, Vue, Watch} from "vue-property-decorator";
import {MdbsCore} from "@/api/tsclient";
import {queryModule} from "@/store";
import {rules} from "@/auxillary/rules";
import HeaderBox from "@/components/query/dialogs/HeaderBox.vue";
import {DateTime} from "luxon";
import MessageBox from "@/components/query/dialogs/MessageBox.vue";
import {answerStateText, MessageType, QueryStage} from "@/components/query/QueryStates";
import IQueryShortDto = MdbsCore.IQueryShortDto;
import IQueryDto = MdbsCore.IQueryDto;
import QueryDto = MdbsCore.QueryDto;
import QueryAnswerDto = MdbsCore.QueryAnswerDto;
import IQueryTypeDefinitionDto = MdbsCore.IQueryTypeDefinitionDto;
import QueryRevokeDto = MdbsCore.QueryRevokeDto;
import QueryAcceptDto = MdbsCore.QueryAcceptDto;
import QuerySolveDto = MdbsCore.QuerySolveDto;
import QueryEditDto = MdbsCore.QueryEditDto;
import QueryDeferDto = MdbsCore.QueryDeferDto;
import QueryConfirmDialog from "@/components/query/dialogs/QueryConfirmDialog.vue";
import {
  canAcceptQuery,
  canAnswerQuery,
  canDeferQuery,
  canEditQuery,
  canRevokeQuery, canSolveQuery
} from "@/components/query/QueryPermissions";

@Component({
  components: {QueryConfirmDialog, HeaderBox, MessageBox}
})
export default class QueryAnswerDialog extends Vue {

  @Prop({required: true})
  query!: IQueryShortDto;

  @Prop({required: true})
  queryTypeDefinition!: IQueryTypeDefinitionDto;

  completeQuery: IQueryDto = new QueryDto();
  newMessage: string = "";
  newText: string = "";
  rules: any = rules;

  textIsReadOnly = true;

  showConfirmDialog = false;
  confirmType = "";
  confirmText = "";

  @Watch('query', {immediate: true})
  async onQueryChange(query: IQueryShortDto | null) {
    const queryId = query?.id;
    if (!queryId) {
      console.error("error passing selected query");
      return;
    }

    this.completeQuery = await queryModule.loadQuery(queryId);
    this.newMessage = "";
    this.newText = query?.text ?? "";
    this.textIsReadOnly = true;

    await this.scrollToMessagesBottom();
  }

  private async scrollToMessagesBottom() {
    this.$nextTick(async () => {
      const scrollTarget = document.getElementById("scrollTarget");
      scrollTarget?.scrollIntoView({block: "end", inline: "nearest"});
    });
  }

  @Watch('queryTypeDefinition', {immediate: true})
  async onQueryDefinitionChange(queryTypeDefinition: IQueryTypeDefinitionDto | null) {
    if (!queryTypeDefinition) {
      console.error("error passing selected query type definition");
      return;
    }

    await this.scrollToMessagesBottom();
  }

  formatDate(date: Date | undefined): string {
    return date ? DateTime.fromJSDate(date).toFormat("dd.MM.yyyy") : "-";
  }

  get queryInfo() {
    if (this.completeQuery.lockedState != "LOCKED" && this.completeQuery.referenceState != "DELETED")
      return "";

    let queryInfo = "&nbsp;(";
    if (this.completeQuery.lockedState == "LOCKED")
      queryInfo = queryInfo + "Gesperrt";
    if (this.completeQuery.lockedState == "LOCKED" && this.completeQuery.referenceState == "DELETED")
      queryInfo = queryInfo + ", ";
    if (this.completeQuery.referenceState == "DELETED")
      queryInfo = queryInfo + "Referenz gelöscht";
    if (this.completeQuery.referenceState == "INVISIBLE")
      queryInfo = queryInfo + "Referenz unsichtbar";
    queryInfo = queryInfo + ")";
    return queryInfo;
  }

  get queryState() {
    // TODO: add translations to def
    let text = "Geschlossen";

    if (this.query.stageState == QueryStage.OPEN)
      text = "Offen";

    if (this.query.stageState == QueryStage.PENDING)
      text = "Ausstehend";

    if (this.query.stageState == QueryStage.NO_LONGER_RELEVANT)
      text = "Nicht mehr relevant";

    if (this.query.stageState == QueryStage.INVISIBLE)
      text = "Unsichtbar";

    return (text ?? "-") + answerStateText(this.query.stageState, this.query.closedReason, this.query.lastComment);
  }

  get canCloseQuery() {
    return this.completeQuery.stageState != QueryStage.CLOSED;
  }

  get canEditQuery() {
    return this.canCloseQuery && canEditQuery(this.queryTypeDefinition);
  }

  get canAcceptQuery() {
    return this.canCloseQuery && canAcceptQuery(this.queryTypeDefinition);
  }

  get canAnswerQuery() {
    return this.canCloseQuery && canAnswerQuery(this.queryTypeDefinition);
  }

  get canRevokeQuery() {
    return !this.query.lastComment && this.canCloseQuery && canRevokeQuery(this.queryTypeDefinition);
  }

  get canSolveQuery() {
    return this.canCloseQuery && canSolveQuery(this.queryTypeDefinition);
  }

  get canDeferQuery() {
    return this.canCloseQuery && canDeferQuery(this.queryTypeDefinition)
        && this.completeQuery.stageState != QueryStage.INVISIBLE
        && this.completeQuery.stageState != QueryStage.NO_LONGER_RELEVANT;
  }

  get revokeText() {
    return "Zurückziehen";
  }

  get acceptText() {
    return "Geprüft/Akzeptiert";
  }

  get solveText() {
    return "Abschließen";
  }

  get deferText() {
    return this.query.stageState == QueryStage.PENDING ? 'Auf neu setzen' : 'Ausstehend';
  }

  toggleTextIsReadonly() {
    if (!this.textIsReadOnly) {
      this.newText = this.completeQuery.text ?? "";
    }
    this.textIsReadOnly = !this.textIsReadOnly;
  }

  get hasMessage() {
    return this.newMessage != "";
  }

  get hasTextChanged() {
    return this.newText != this.completeQuery.text;
  }

  handleCloseConfirm() {
    if (this.confirmType == "revoke")
      this.revoke();
    if (this.confirmType == "accept")
      this.accept();
    if (this.confirmType == "solve")
      this.solve();
    if (this.confirmType == "defer")
      this.defer();
    this.showConfirmDialog = false;
    this.confirmType = "";
    this.confirmText = "";
  }

  @Emit("cancel")
  cancel() {
  }

  @Emit("revoke")
  revoke() {
    const revokeDto = new QueryRevokeDto();
    revokeDto.id = this.completeQuery.id;
    revokeDto.revoke = true;
    return revokeDto;
  }

  @Emit("accept")
  accept() {
    const acceptDto = new QueryAcceptDto();
    acceptDto.id = this.completeQuery.id;
    acceptDto.accept = true;
    return acceptDto;
  }

  @Emit("solve")
  solve() {
    const solveDto = new QuerySolveDto();
    solveDto.id = this.completeQuery.id;
    solveDto.solve = true;
    return solveDto;
  }

  @Emit("defer")
  defer() {
    const deferDto = new QueryDeferDto();
    deferDto.id = this.completeQuery.id;
    deferDto.pending = this.query.stageState == QueryStage.OPEN ? true : false;
    this.showConfirmDialog = false;
    return deferDto;
  }

  @Emit("edit")
  edit() {
    const editDto = new QueryEditDto();
    editDto.id = this.completeQuery.id;
    editDto.text = this.newText;
    return editDto;
  }

  @Emit("saveAnswer")
  saveAnswer() {
    const answerDto = new QueryAnswerDto();
    answerDto.id = this.completeQuery.id;
    answerDto.message = this.newMessage;

    return answerDto;
  }

  get messages() {
    return this.completeQuery.messages?.filter(m => m.messageType === MessageType.COMMENT) ?? [];
  }

  formatMessage(message: any) {
    const text = message.Message ?? "-";
    return text;
  }

}
